import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
  ElementRef,
  effect,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StudentNav } from '../../../common/student-nav/student-nav';
import {
  EventService,
  ApiEvent,
  EventResponse,
  eventResponseOf,
} from '../../../../services/event.service';
import { Router } from '@angular/router';
import { MeetingService } from '../../../../services/meeting.service';
import { EventBuilder } from '../../../common/event-builder/event-builder';
import { EventDetail } from '../../../common/event-detail/event-detail';
import { OverlapEvents, OverlapEventsData } from '../../../common/overlap-events/overlap-events';
import { meetingAsEvent, scheduledMeetings } from '../../../../helpers/calendar-meetings';
import { injectFocusTarget } from '../../../../helpers/focus-target';
import {
  layoutDay,
  DayLayout,
  OverflowTile,
  PositionedEvent,
  TimedEvent,
} from '../../../../helpers/event-helpers';

export interface CalEvent {
  /**
   * Track key for the grid. Ids are unique per source table, not across them,
   * so anything the calendar draws alongside events — meetings first — would
   * collide on `id` alone and make @for reject the duplicate.
   */
  key: string;
  id: number;
  title: string;
  date: Date;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  /** A meeting is drawn as an event and differs only in its colour and where a
   *  click goes — see meetingAsEvent(). */
  type: 'personal' | 'shared' | 'meeting';
  response: EventResponse | null;
  raw: ApiEvent;
}

type TimedCalEvent = CalEvent & TimedEvent;
export type PositionedCalEvent = PositionedEvent<TimedCalEvent>;

@Component({
  selector: 'app-student-calendar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatCardModule,
    MatRippleModule,
    MatDialogModule,
    StudentNav,
  ],
  templateUrl: './student-calendar.html',
  styleUrl: './student-calendar.css',
})
export class StudentCalendar implements OnInit {
  private eventService = inject(EventService);
  private meetingService = inject(MeetingService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private weekBody = viewChild<ElementRef<HTMLDivElement>>('weekBody');
  private dayBody = viewChild<ElementRef<HTMLDivElement>>('dayBody');

  private readonly HOUR_HEIGHT = 60;
  private readonly SCROLL_TO_HOUR = 7;

  constructor() {
    effect(() => {
      const week = this.weekBody()?.nativeElement;
      const day = this.dayBody()?.nativeElement;
      const el = week ?? day;
      if (!el) return;
      requestAnimationFrame(() => {
        el.scrollTop = this.SCROLL_TO_HOUR * this.HOUR_HEIGHT;
      });
    });
  }

  today = new Date();
  viewMode = signal<'month' | 'week' | 'day'>('week');
  anchor = signal<Date>(new Date());
  /**
   * Personal events, shared events, and meetings load independently — each has
   * its own request and its own list, so a failure loading one never hides the
   * others. The grid shows all three.
   */
  private personalItems = signal<CalEvent[]>([]);
  /** Every shared event for this student, including ones they haven't accepted. */
  private allSharedItems = signal<CalEvent[]>([]);
  /** Only accepted shared events go on the grid. */
  private sharedItems = computed(() =>
    this.allSharedItems().filter((e) => e.response === 'confirmed'),
  );
  private meetingItems = signal<CalEvent[]>([]);
  events = computed(() => [...this.personalItems(), ...this.sharedItems(), ...this.meetingItems()]);

  private focusTarget = injectFocusTarget<CalEvent>({
    param: 'eventId',
    items: () => [...this.personalItems(), ...this.allSharedItems()],
    onFocus: (event) => {
      this.anchor.set(new Date(event.date));
      this.openEventDialog(event);
    },
  });

  hours = Array.from({ length: 24 }, (_, i) => i);

  ngOnInit(): void {
    this.loadEvents();
    this.loadMeetings();
  }

  loadEvents(): void {
    this.eventService.getEvents().subscribe({
      next: (personal) => {
        this.personalItems.set(personal.map((e) => this.toCalEvent(e, 'personal')));
        this.focusTarget.check();
      },
      error: (err) => console.error('Failed to load personal events:', err),
    });

    this.eventService.getSharedEvents().subscribe({
      next: (shared) => {
        this.allSharedItems.set(shared.map((e) => this.toCalEvent(e, 'shared')));
        this.focusTarget.check();
      },
      error: (err) => console.error('Failed to load shared events:', err),
    });
  }

  loadMeetings(): void {
    this.meetingService.getMeetings().subscribe({
      next: (meetings) =>
        this.meetingItems.set(
          scheduledMeetings(meetings).map((m) =>
            this.toCalEvent(meetingAsEvent(m, m.meeting.facultyName), 'meeting'),
          ),
        ),
      error: (err) => console.error('Failed to load meetings:', err),
    });
  }

  private toCalEvent(e: ApiEvent, type: CalEvent['type']): CalEvent {
    const date = new Date(e.day + 'T00:00:00');
    const [startHour, startMinute] = e.start_time.split(':').map(Number);
    const [endHour, endMinute] = e.end_time
      ? e.end_time.split(':').map(Number)
      : [startHour + 1, startMinute];
    return {
      key: `${type === 'meeting' ? 'meeting' : 'event'}-${e.id}`,
      id: e.id,
      title: e.title,
      date,
      startHour,
      startMinute,
      endHour,
      endMinute,
      type,
      response: eventResponseOf(e),
      raw: e,
    };
  }

  openAddEventDialog(): void {
    const ref = this.dialog.open(EventBuilder, {
      width: '560px',
      maxWidth: '95vw',
      data: null,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.action === 'saved') this.loadEvents();
    });
  }

  openEventDialog(event: CalEvent): void {
    if (event.type === 'meeting') {
      // The meetings page owns a meeting's details and the actions on it, so
      // the calendar hands off rather than keeping a second copy of them. Same
      // deep link a meeting notification uses.
      this.router.navigate(['/meetings'], { queryParams: { meetingId: event.id } });
      return;
    }
    if (event.raw.is_shared) {
      const ref = this.dialog.open(EventDetail, {
        width: '480px',
        maxWidth: '95vw',
        data: { event: event.raw },
      });
      ref.afterClosed().subscribe(() => this.loadEvents());
      return;
    }
    const ref = this.dialog.open(EventBuilder, {
      width: '560px',
      maxWidth: '95vw',
      data: event.raw,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.action === 'saved' || result?.action === 'deleted') this.loadEvents();
    });
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  monthLabel = computed(() =>
    this.anchor().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
  );

  weekDays = computed(() => {
    const base = new Date(this.anchor());
    const sun = new Date(base);
    sun.setDate(base.getDate() - base.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(sun);
      d.setDate(sun.getDate() + i);
      return d;
    });
  });

  selectedDay = computed(() => new Date(this.anchor()));

  monthGrid = computed(() => {
    const year = this.anchor().getFullYear();
    const month = this.anchor().getMonth();
    const first = new Date(year, month, 1).getDay();
    const total = new Date(year, month + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  });

  // ── Navigation ─────────────────────────────────────────────────────────────

  prev(): void {
    const d = new Date(this.anchor());
    const v = this.viewMode();
    if (v === 'week') d.setDate(d.getDate() - 7);
    if (v === 'month') d.setMonth(d.getMonth() - 1);
    if (v === 'day') d.setDate(d.getDate() - 1);
    this.anchor.set(d);
  }

  next(): void {
    const d = new Date(this.anchor());
    const v = this.viewMode();
    if (v === 'week') d.setDate(d.getDate() + 7);
    if (v === 'month') d.setMonth(d.getMonth() + 1);
    if (v === 'day') d.setDate(d.getDate() + 1);
    this.anchor.set(d);
  }

  goToToday(): void {
    this.anchor.set(new Date());
  }

  selectDay(date: Date): void {
    this.anchor.set(new Date(date));
    this.viewMode.set('day');
  }

  setView(v: 'month' | 'week' | 'day'): void {
    this.viewMode.set(v);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  isToday(date: Date): boolean {
    return date?.toDateString() === this.today.toDateString();
  }

  isSameDay(a: Date, b: Date): boolean {
    return a.toDateString() === b.toDateString();
  }

  eventsForDay(date: Date): CalEvent[] {
    return this.events().filter((e) => this.isSameDay(e.date, date));
  }

  isCompact(event: CalEvent): boolean {
    return this.eventHeight(event) < 50;
  }

  eventTop(event: CalEvent): number {
    return (event.startHour + event.startMinute / 60) * this.HOUR_HEIGHT;
  }

  eventHeight(event: CalEvent): number {
    const startMins = event.startHour * 60 + event.startMinute;
    const endMins = event.endHour * 60 + event.endMinute;
    return ((endMins - startMins) / 60) * this.HOUR_HEIGHT - 4;
  }

  private toTimed(e: CalEvent): TimedCalEvent {
    return { ...e, start: e.startHour * 60 + e.startMinute, end: e.endHour * 60 + e.endMinute };
  }

  dayLayout(date: Date): DayLayout<TimedCalEvent> {
    const maxCols = this.viewMode() === 'day' ? 4 : 3;
    return layoutDay(
      this.eventsForDay(date).map((e) => this.toTimed(e)),
      maxCols,
    );
  }

  eventWidth(cols: number): string {
    return `calc((100% - 2 * var(--cc-ev-inset) - ${(cols - 1) * 2}px) / ${cols})`;
  }

  eventLeft(col: number, cols: number): string {
    const colWidth = `((100% - 2 * var(--cc-ev-inset) - ${(cols - 1) * 2}px) / ${cols})`;
    return `calc(var(--cc-ev-inset) + ${col} * (${colWidth} + 2px))`;
  }

  minsTop(mins: number): number {
    return (mins / 60) * this.HOUR_HEIGHT;
  }

  minsHeight(startMins: number, endMins: number): number {
    return ((endMins - startMins) / 60) * this.HOUR_HEIGHT - 4;
  }

  openOverflow(overflow: OverflowTile<TimedCalEvent>): void {
    const ref = this.dialog.open<OverlapEvents, OverlapEventsData, CalEvent>(OverlapEvents, {
      width: '360px',
      maxWidth: '95vw',
      data: { events: overflow.hidden },
    });
    ref.afterClosed().subscribe((chosen) => {
      if (chosen) this.openEventDialog(chosen);
    });
  }

  hourLabel(h: number): string {
    if (h === 0) return '12am';
    if (h < 12) return `${h}am`;
    if (h === 12) return '12pm';
    return `${h - 12}pm`;
  }

  timeLabel(hour: number, minute: number): string {
    const period = hour >= 12 ? 'pm' : 'am';
    const h = hour % 12 || 12;
    const m = minute.toString().padStart(2, '0');
    return minute === 0 ? `${h}${period}` : `${h}:${m}${period}`;
  }

  dayShort(date: Date): string {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  weekRangeLabel(): string {
    const days = this.weekDays();
    const first = days[0];
    const last = days[6];
    return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  dayViewLabel(): string {
    return this.anchor().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  monthDayNums = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
}
